import React from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useFlexibleTranslation } from '../contexts/LanguageContext';
import msLogo from "../images/microsoft-logo.png";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Container = styled.div`
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  min-height: 100vh; background: linear-gradient(135deg, #f5f7fa 0%, #e4e7eb 100%); padding: 20px;
`;

const Card = styled.div`
  background: white; border-radius: 24px; box-shadow: 0 12px 24px rgba(0,0,0,0.1);
  padding: 40px; width: 100%; max-width: 480px; text-align: center; animation: ${fadeIn} 0.6s ease-out;
  display: flex; flex-direction: column; align-items: center;
`;

const Logo = styled.img` width: 240px; margin-bottom: 32px; filter: drop-shadow(0 4px 8px rgba(0,0,0,0.1)); `;
const Title = styled.h1` color: #2d3748; font-size: 24px; margin: 0 0 16px; font-weight: 600; `;
const Subtitle = styled.p` color: #718096; font-size: 16px; margin: 0 0 32px; line-height: 1.5; `;

const Button = styled.button`
  background-color: #ff6b35; color: white; border: none; padding: 16px 32px; border-radius: 12px;
  font-size: 16px; font-weight: 600; cursor: pointer; transition: all 0.2s ease; width: 100%; max-width: 320px;
  display: flex; align-items: center; justify-content: center; gap: 8px;
  &:hover { background-color: #e65a2a; transform: translateY(-1px); box-shadow: 0 4px 12px rgba(255,107,53,0.2); }
  &:active { transform: translateY(0); }
  &:disabled { background-color: #cbd5e0; cursor: not-allowed; transform: none; }
  svg { width: 20px; height: 20px; }
`;

const SecondaryButton = styled(Button)`
  background-color: #334155;
  margin-top: 12px;
  &:hover { background-color: #1f2937; }
`;

const Version = styled.div` position: fixed; bottom: 16px; right: 16px; color: #a0aec0; font-size: 14px; `;

const Landing = () => {
  const { t } = useFlexibleTranslation();
  const navigate = useNavigate();
  const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";

  const handleMicrosoftSSO = () => {
    window.location.assign(`${API_BASE}/msft/connect-start`);
  };
  const handleGoogleSSO = () => {
    window.location.assign(`${API_BASE}/google/connect-start`);
  };

  const handleGoToSignup = () => {
    navigate('/auth/signup'); 
  };
  const handleEmailSignIn = () => {
    navigate('/auth/signin'); 
  };

  return (
    <Container>
      <Card>
        <Logo src="/CODiii_PrimaryGradient1.png" alt="CODiii Logo" />
        <Title>{t('landing.welcomeToCodiii', 'Welcome to CODiii')}</Title>
        <Subtitle>
          {t('landing.intelligentBuildingLifecycle', 'Your intelligent building lifecycle management platform.')}
          {' '}
          {t('landing.signInToStart', 'Sign in to start exploring and managing your projects.')}
        </Subtitle>

        {/* hidden for now — email/password sign-in + signup
        <Button onClick={handleEmailSignIn}>
          <svg viewBox="0 0 24 24">
            <path fill="currentColor" d="M12,4A4,4 0 0,1 16,8A4,4 0 0,1 12,12A4,4 0 0,1 8,8A4,4 0 0,1 12,4M12,14C16.42,14 20,15.79 20,18V20H4V18C4,15.79 7.58,14 12,14Z" />
          </svg>
          {t('landing.signInWithEmail', 'Sign in with Email')}
        </Button>

        <SecondaryButton onClick={handleGoToSignup} style={{ backgroundColor: 'transparent', color: '#334155', border: '1px solid #cbd5e0' }}>
          {t('landing.createAccount', 'Create account')}
        </SecondaryButton>
        */}
        <SecondaryButton
          onClick={handleMicrosoftSSO}
          style={{
            marginTop: 12,
            backgroundColor: "#fff",
            color: "#111827",
            border: "1px solid #e5e7eb",
          }}
        >
          <img
            src={msLogo}
            alt=""
            aria-hidden="true"
            style={{ width: 18, height: 18, display: "block" }}
          />
          Continue with Microsoft
        </SecondaryButton>
        <button
          onClick={handleGoogleSSO}
          style={{
            marginTop: 12,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 0,
            width: "100%",
            maxWidth: 320,
            height: 48,
            backgroundColor: "#fff",
            border: "1px solid #dadce0",
            borderRadius: 4,
            cursor: "pointer",
            fontFamily: "Roboto, sans-serif",
            fontSize: 16,
            fontWeight: 500,
            color: "#757575",
            letterSpacing: "0.02em",
            boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
            padding: 0,
          }}
        >
          <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 48,
            height: 48,
            borderRight: "1px solid #dadce0",
            flexShrink: 0,
          }}>
            <svg viewBox="0 0 24 24" style={{ width: 18, height: 18 }}>
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
          </div>
          <span style={{ flex: 1, textAlign: "center" }}>Sign in with Google</span>
        </button>
      </Card>
      <p style={{ marginTop: 24, fontSize: 13, color: '#a0aec0' }}>
        By continuing, you agree to our{' '}
        <a href="https://codiii.com/terms-of-service" target="_blank" rel="noopener noreferrer" style={{ color: '#718096' }}>Terms of Service</a>
        {' '}and{' '}
        <a href="https://codiii.com/privacy-policy" target="_blank" rel="noopener noreferrer" style={{ color: '#718096' }}>Privacy Policy</a>
      </p>
      <Version>v1.0.0</Version>
    </Container>
  );
};

export default Landing;
